import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import io from 'socket.io-client';
import { 
  Send, Image, X, Info, Trash2, User, Check, CheckCheck,
  MessageSquare, MessageCircle, Users as UsersIcon, Lock, Search,
  LogOut, ChevronLeft, Copy, Heart, Share2, UserPlus,
  Globe, Clock, Mail, Phone, Video, Paperclip, Smile,
  MoreVertical, Repeat, Plus, ArrowLeft, Crown, BadgeCheck,
  Sparkles, HandMetal, Waves
} from 'lucide-react';
import PostModal from './PostModal.jsx';
import { useTranslation } from 'react-i18next';
import { isGroupUnread, markGroupAsRead } from '../utils/messageBadge.js';
import { API_BASE_URL, SOCKET_URL, resolveMediaUrl } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';

function Messages() {
  const { t, i18n } = useTranslation();
  const { user, makeApiCall, loadAvailableUsersForMessages } = useAuth();
  const socketRef = useRef(null);
  const navigate = useNavigate();
  const { type, id } = useParams();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState(type === 'group' ? 'groups' : 'direct');
  const [conversations, setConversations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [emojiGroup, setEmojiGroup] = useState('recent');
  const [recentEmojis, setRecentEmojis] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showConversationInfo, setShowConversationInfo] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);

  // ── Mobile: track whether user is viewing chat or sidebar ──
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [openPostModalId, setOpenPostModalId] = useState(null);
  
  const isSendingRef = useRef(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const emojiPanelRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const activeConversationRef = useRef(null);
  const activeGroupRef = useRef(null);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return resolveMediaUrl(imagePath);
  };

  const UserAvatar = ({ person, style = {} }) => {
    const [imgError, setImgError] = useState(false);
    const imgUrl = person?.profileImage ? getImageUrl(person.profileImage) : null;
    const name = person?.name || person?.username || '?';
    const initial = name.charAt(0).toUpperCase();
    if (!imgUrl || imgError) {
      return (
        <div style={{
          ...style,
          background: 'linear-gradient(135deg, #32614d, #2a5240)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800,
          fontSize: style.width ? `${parseInt(style.width) * 0.38}px` : '1.1rem',
          flexShrink: 0, userSelect: 'none',
        }}>{initial}</div>
      );
    }
    return <img src={imgUrl} alt={name} style={style} onError={() => setImgError(true)} />;
  };

  const GroupAvatar = ({ group, style = {} }) => {
    const [imgError, setImgError] = useState(false);
    const imgUrl = group?.image ? getImageUrl(group.image) : null;
    const initial = (group?.name || 'G').charAt(0).toUpperCase();
    if (!imgUrl || imgError) {
      return (
        <div style={{
          ...style,
          background: 'linear-gradient(135deg, #32614d, #2a5240)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800,
          fontSize: style.width ? `${parseInt(style.width) * 0.4}px` : '1.2rem',
          flexShrink: 0, userSelect: 'none',
        }}>{initial}</div>
      );
    }
    return <img src={imgUrl} alt={group?.name} style={style} onError={() => setImgError(true)} />;
  };

  const formatTime = (date) => {
    if (!date) return '';
    const messageDate = new Date(date);
    const now = new Date();
    const diff = now - messageDate;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return t('pages.messages.justNow');
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    const locale = i18n.language === 'hy' ? 'hy-AM' : i18n.language === 'ru' ? 'ru-RU' : 'en-US';
    return messageDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  };

  const formatConversationTime = (date) => {
    if (!date) return '';
    const messageDate = new Date(date);
    const now = new Date();
    const diff = now - messageDate;
    const hours = Math.floor(diff / 3600000);
    if (hours < 24) return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); };

  const getOtherParticipant = (conversation) => {
    if (!conversation || !conversation.participants || !user) return null;
    const other = conversation.participants.find(p => (p._id || p.id || p) !== (user._id || user.id));
    return other || conversation.participants[0] || null;
  };

  const isGroupAdmin = (group) => {
    if (!group || !user) return false;
    const createdById = typeof group.createdBy === 'object' ? group.createdBy._id : group.createdBy;
    return createdById === user._id;
  };

  const isGroupMember = (group) => {
    if (!group || !user) return false;
    const isInMembers = group.members?.some(m => (typeof m === 'object' ? m._id : m) === user._id);
    const isCreator = group.createdBy === user._id || (typeof group.createdBy === 'object' && group.createdBy._id === user._id);
    return isInMembers || isCreator || group.isMember;
  };

  const loadConversations = async () => {
    try {
      setConversationsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) { setConversationsLoading(false); return; }
      const response = await fetch(`${API_BASE_URL}/conversations`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        if (id && type === 'dm') {
          const conv = data.conversations?.find(c => c._id === id);
          if (conv) { setActiveConversation(conv); setActiveTab('direct'); setMobileChatOpen(true); }
        }
      } else { setConversations([]); }
    } catch { setConversations([]); }
    finally { setConversationsLoading(false); setLoading(false); }
  };

  const loadGroups = async () => {
    try {
      setGroupsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) { setGroupsLoading(false); return; }
      const response = await fetch(`${API_BASE_URL}/community/my-groups`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const allGroups = [...(data.createdGroups || []), ...(data.memberGroups || [])];
        setGroups(allGroups);
        if (id && type === 'group') {
          const group = allGroups.find(g => g._id === id);
          if (group) { setActiveGroup(group); setActiveTab('groups'); setMobileChatOpen(true); }
        }
      } else { setGroups([]); }
    } catch { setGroups([]); }
    finally { setGroupsLoading(false); setLoading(false); }
  };

  const loadMessages = async (conversationId) => {
    try {
      setMessagesLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const msgs = data.messages || [];
        processedMessageIds.current = new Set(msgs.map(m => m._id));
        setMessages(msgs);
        setTimeout(scrollToBottom, 100);
      } else { setMessages([]); }
    } catch { setMessages([]); }
    finally { setMessagesLoading(false); }
  };

  const loadGroupMessages = async (groupId) => {
    try {
      setMessagesLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/community/groups/${groupId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const msgs = data.messages || [];
        processedMessageIds.current = new Set(msgs.map(m => m._id));
        setMessages(msgs);
        setTimeout(scrollToBottom, 100);
      } else { setMessages([]); }
    } catch { setMessages([]); }
    finally { setMessagesLoading(false); }
  };

  const loadAvailableUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setAvailableUsers([]); return; }
      const res = await loadAvailableUsersForMessages();
      if (res?.success && res.users?.length > 0) { setAvailableUsers(res.users); return; }
      const response = await fetch(`${API_BASE_URL}/users/available`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) { const data = await response.json(); setAvailableUsers(data.users || data.data || []); }
      else { setAvailableUsers([]); }
    } catch { setAvailableUsers([]); }
  };

  const markAsRead = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/conversations/${conversationId}/read`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setConversations(prev => prev.map(c => c._id === conversationId ? { ...c, unreadCount: 0 } : c));
    } catch {}
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) { setError(t('pages.messages.errNoContent')); return; }
    if (!activeConversation) { setError(t('pages.messages.errNoConversation')); return; }
    if (isSendingRef.current) return;
    isSendingRef.current = true; setSending(true); setError(null);
    const messageContent = newMessage.trim(); const fileToSend = selectedFile;
    setNewMessage(''); setSelectedFile(null); setFilePreview(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      if (messageContent) formData.append('content', messageContent);
      if (fileToSend) formData.append('image', fileToSend);
      const response = await fetch(`${API_BASE_URL}/conversations/${activeConversation._id}/messages`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (response.ok) {
        const data = await response.json(); const sentMessage = data.message;
        if (!processedMessageIds.current.has(sentMessage._id)) {
          processedMessageIds.current.add(sentMessage._id);
          setMessages(prev => [...prev, sentMessage]);
        }
        setConversations(prev => {
          const updated = prev.map(c => c._id === activeConversation._id ? { ...c, lastMessage: sentMessage, updatedAt: new Date() } : c);
          const cur = updated.find(c => c._id === activeConversation._id);
          return [cur, ...updated.filter(c => c._id !== activeConversation._id)];
        });
        setTimeout(scrollToBottom, 50);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || t('pages.messages.errSendMessage')); setNewMessage(messageContent);
      }
    } catch (error) { setError(error.message); setNewMessage(messageContent); }
    finally { setSending(false); isSendingRef.current = false; }
  };

  const handleMessageLike = async (messageId) => {
    if (!messageId || !user) return;
    try {
      const token = localStorage.getItem('token');
      const message = messages.find(m => m._id === messageId);
      if (!message) return;
      const isLiked = message.likes?.includes(user._id) || false;
      setMessages(prev => prev.map(m => {
        if (m._id === messageId) {
          const currentLikes = Array.isArray(m.likes) ? m.likes : [];
          return { ...m, likes: isLiked ? currentLikes.filter(id => id !== user._id) : [...currentLikes, user._id] };
        }
        return m;
      }));
      try {
        await fetch(`${API_BASE_URL}/messages/${messageId}/like`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
      } catch {}
    } catch {}
  };

  const handleMessageReply = (message) => {
    if (!message) return;
    setNewMessage(`↩ ${message.sender?.name}: ${message.content?.substring(0, 40)}...\n`);
    setTimeout(() => {
      const input = document.querySelector('.msg-textarea');
      if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    }, 100);
  };

  const insertEmoji = (emoji) => {
    if (!emoji) return;
    const ta = textareaRef.current;
    // If the textarea is available and focused, insert at cursor.
    if (ta && document.activeElement === ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart ?? newMessage.length;
      const end = ta.selectionEnd ?? start;
      const next = `${newMessage.slice(0, start)}${emoji}${newMessage.slice(end)}`;
      setNewMessage(next);
      setTimeout(() => {
        try {
          const pos = start + String(emoji).length;
          ta.focus();
          ta.setSelectionRange(pos, pos);
        } catch {}
      }, 0);
      return;
    }
    // Otherwise, append.
    setNewMessage(prev => `${prev}${emoji}`);
  };

  const handleEmojiPick = (emoji) => {
    // Update recent emojis (keep newest first).
    setRecentEmojis(prev => {
      const cleaned = Array.isArray(prev) ? prev.filter(e => e !== emoji) : [];
      return [emoji, ...cleaned].slice(0, 12);
    });
    insertEmoji(emoji);
    setEmojiPickerOpen(false);
  };

  useEffect(() => {
    if (!emojiPickerOpen) return;
    const onDocMouseDown = (e) => {
      const panel = emojiPanelRef.current;
      const btn = emojiButtonRef.current;
      if (panel && panel.contains(e.target)) return;
      if (btn && btn.contains(e.target)) return;
      setEmojiPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [emojiPickerOpen]);

  const EMOJI_GROUPS = {
    recent: { label: t('pages.messages.emojiRecent') },
    smileys: { label: t('pages.messages.emojiSmileys'), emojis: ['😀', '😁', '😂', '🤣', '😊', '😎', '😍', '😘', '😜', '🤩', '🥳', '😇'] },
    hearts: { label: t('pages.messages.emojiHearts'), emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🖤', '💖', '💘', '💕', '💞'] },
    hands: { label: t('pages.messages.emojiHands'), emojis: ['👏', '🙌', '🤝', '🙏', '✌️', '🤞', '👌', '🫶', '💪', '🤲', '👐', '🧡'] },
    animals: { label: t('pages.messages.emojiAnimals'), emojis: ['🐶', '🐱', '🐻', '🐼', '🐨', '🦊', '🐸', '🐵', '🦄', '🐯', '🐮', '🐷'] },
    food: { label: t('pages.messages.emojiFood'), emojis: ['🍕', '🍔', '🌮', '🍣', '🍜', '🍩', '🍪', '🍫', '☕', '🍉', '🥑', '🍞'] },
    travel: { label: t('pages.messages.emojiTravel'), emojis: ['✈️', '🚗', '🚆', '🗺️', '🏝️', '🏙️', '🌋', '🧳', '🚀', '🚶', '🛶', '🏕️'] },
    symbols: { label: t('pages.messages.emojiSymbols'), emojis: ['✨', '🔥', '💫', '⭐', '⚡', '❄️', '🌙', '☀️', '☂️', '🎉', '🎈', '📌'] },
    weather: { label: t('pages.messages.emojiWeather'), emojis: ['☀️', '🌤️', '☁️', '🌧️', '⛈️', '🌩️', '🌈', '❄️', '🍃', '🌾', '🌀', '🧊'] },
  };

  const emojiGroupOrder = ['recent', 'smileys', 'hearts', 'hands', 'animals', 'food', 'travel', 'symbols', 'weather'];
  const defaultRecent = ['😊', '😍', '😂', '❤️', '🙏', '✨', '🎉', '👍'];
  const currentEmojiList = (() => {
    if (emojiGroup === 'recent') return (recentEmojis.length > 0 ? recentEmojis : defaultRecent).slice(0, 20);
    return EMOJI_GROUPS[emojiGroup]?.emojis || [];
  })();

  const EmojiPicker = ({ variant = 'panel' }) => (
    <div
      ref={variant === 'panel' ? emojiPanelRef : null}
      className={`emoji-picker ${variant === 'modal' ? 'emoji-picker-modal' : 'emoji-picker-panel'}`}
    >
      <div className="emoji-groups">
        {emojiGroupOrder.map((gid) => (
          <button
            key={gid}
            type="button"
            className={`emoji-group-btn ${emojiGroup === gid ? 'active' : ''}`}
            onClick={() => setEmojiGroup(gid)}
          >
            {EMOJI_GROUPS[gid]?.label || t('pages.messages.emojiRecent')}
          </button>
        ))}
      </div>
      <div className="emoji-grid">
        {currentEmojiList.map((em) => (
          <button
            key={em}
            type="button"
            className="emoji-emoji-btn"
            onClick={() => handleEmojiPick(em)}
          >
            {em}
          </button>
        ))}
      </div>
    </div>
  );

  const handleDeleteMessage = async (messageId) => {
    if (!messageId) return;
    if (!window.confirm(t('pages.messages.confirmDeleteMessage'))) return;
    const backup = messages;
    setMessages(prev => prev.filter(m => m._id !== messageId));
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // 1) Try simple endpoint (if it exists)
      let res = await fetch(`${API_BASE_URL}/messages/${messageId}`, { method: 'DELETE', headers });
      if (res.ok) return;

      // 2) Try conversation-specific endpoint
      if (activeConversation?._id) {
        res = await fetch(`${API_BASE_URL}/conversations/${activeConversation._id}/messages/${messageId}`, {
          method: 'DELETE',
          headers,
        });
        if (res.ok) return;
      }

      // 3) Try group-specific endpoint
      if (activeGroup?._id) {
        res = await fetch(`${API_BASE_URL}/community/groups/${activeGroup._id}/messages/${messageId}`, {
          method: 'DELETE',
          headers,
        });
        if (res.ok) return;
      }

      throw new Error('Delete failed');
    } catch (e) {
      console.error('Delete message error:', e);
      setMessages(backup);
      setError(t('pages.messages.errDeleteMessage'));
    }
  };

  const sendGroupMessage = async () => {
    if (!newMessage.trim() && !selectedFile) { setError(t('pages.messages.errNoContent')); return; }
    if (!activeGroup) { setError(t('pages.messages.errNoGroup')); return; }
    if (isSendingRef.current) return;
    isSendingRef.current = true; setSending(true); setError(null);
    const messageContent = newMessage.trim(); const fileToSend = selectedFile;
    setNewMessage(''); setSelectedFile(null); setFilePreview(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      if (messageContent) formData.append('content', messageContent);
      if (fileToSend) formData.append('image', fileToSend);
      const response = await fetch(`${API_BASE_URL}/community/groups/${activeGroup._id}/messages`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (response.ok) {
        const data = await response.json(); const sentMessage = data.message;
        if (!processedMessageIds.current.has(sentMessage._id)) {
          processedMessageIds.current.add(sentMessage._id);
          setMessages(prev => [...prev, sentMessage]);
        }
        setGroups(prev => {
          const updated = prev.map(g => g._id === activeGroup._id ? { ...g, lastMessage: sentMessage, updatedAt: new Date() } : g);
          const cur = updated.find(g => g._id === activeGroup._id);
          return [cur, ...updated.filter(g => g._id !== activeGroup._id)];
        });
        setTimeout(scrollToBottom, 50);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || t('pages.messages.errSendMessage')); setNewMessage(messageContent);
      }
    } catch (error) { setError(error.message); setNewMessage(messageContent); }
    finally { setSending(false); isSendingRef.current = false; }
  };

  const createNewConversation = async (participantId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ participantIds: [participantId], isGroup: false })
      });
      if (response.ok) {
        const data = await response.json(); const newConversation = data.conversation;
        setConversations(prev => [newConversation, ...prev]);
        setActiveConversation(newConversation); setActiveTab('direct'); setMobileChatOpen(true);
        setShowNewChatModal(false); navigate(`/messages/dm/${newConversation._id}`);
        return newConversation;
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('pages.messages.errCreateConversation'));
      }
    } catch (error) { setError(error.message); throw error; }
    finally { setLoading(false); }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm(t('pages.messages.confirmDeleteConversation'))) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setConversations(prev => prev.filter(c => c._id !== conversationId));
        if (activeConversation?._id === conversationId) { setActiveConversation(null); setMessages([]); setMobileChatOpen(false); navigate('/messages'); }
        setShowConversationInfo(false); setSuccessMessage(t('pages.messages.conversationDeleted'));
      }
    } catch { setError(t('pages.messages.errDeleteConversation')); }
  };

  const leaveGroup = async (groupId) => {
    if (!window.confirm(t('pages.messages.confirmLeaveGroup'))) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/community/groups/${groupId}/leave`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setGroups(prev => prev.filter(g => g._id !== groupId));
        if (activeGroup?._id === groupId) { setActiveGroup(null); setMessages([]); setMobileChatOpen(false); navigate('/messages'); }
        setShowGroupInfo(false); setSuccessMessage(t('pages.messages.leftGroupSuccess'));
      }
    } catch { setError(t('pages.messages.errLeaveGroup')); }
  };

  const joinGroup = async (groupId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/community/groups/${groupId}/join`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setGroups(prev => prev.map(g => g._id === groupId ? { ...g, isMember: true, membersCount: (g.membersCount || 0) + 1 } : g));
        if (activeGroup?._id === groupId) setActiveGroup(prev => ({ ...prev, isMember: true }));
        setSuccessMessage(t('pages.messages.joinedGroupSuccess'));
      }
    } catch { setError(t('pages.messages.errJoinGroup')); }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError(t('pages.messages.errFileTooLarge')); return; }
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) { setError(t('pages.messages.errSelectImage')); return; }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setFilePreview(e.target.result);
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleTyping = () => {
    if (!socketRef.current?.connected) return;
    if (activeGroup) {
      socketRef.current.emit('group-typing', { groupId: activeGroup._id, userId: user._id, userName: user.name || user.username, isTyping: true });
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current?.emit('group-typing', { groupId: activeGroup._id, userId: user._id, userName: user.name || user.username, isTyping: false });
      }, 2000);
    } else if (activeConversation) {
      socketRef.current.emit('typing', { conversationId: activeConversation._id, userId: user._id, userName: user.name || user.username });
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current?.emit('stopTyping', { conversationId: activeConversation._id, userId: user._id });
      }, 2000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (activeGroup) sendGroupMessage(); else if (activeConversation) sendMessage();
    }
  };

  // ── Back from chat to sidebar (mobile) ──
  const handleMobileBack = () => {
    setMobileChatOpen(false);
    setActiveConversation(null);
    setActiveGroup(null);
    setMessages([]);
    navigate('/messages');
  };

  useEffect(() => {
    activeConversationRef.current = activeConversation;
    activeGroupRef.current = activeGroup;
  }, [activeConversation, activeGroup]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    socketRef.current = io(SOCKET_URL, {
      query: { token }, transports: ['websocket', 'polling'],
      reconnection: true, reconnectionAttempts: 5, reconnectionDelay: 1000
    });
    const socket = socketRef.current;
    socket.on('connect', () => {
      // Without this, this socket only ever hears messages for whichever single
      // conversation/group room it has explicitly joined — a message arriving in any other
      // conversation of this user's never reaches this page until it's reloaded, since the
      // backend also broadcasts every new message to the recipient's user room for exactly
      // this "conversation list should update live" case (see /conversations/:id/messages).
      socket.emit('join-user-room', user._id || user.id);
      if (activeConversationRef.current?._id) {
        socket.emit('join-conversation-room', activeConversationRef.current._id);
      }
      if (activeGroupRef.current?._id) {
        socket.emit('join-group-chat', activeGroupRef.current._id);
      }
    });
    const handleIncomingMessage = (data) => {
      const currentConversation = activeConversationRef.current;
      if (currentConversation && data.conversationId === currentConversation._id) {
        const msgId = data.message._id;
        if (!processedMessageIds.current.has(msgId)) {
          processedMessageIds.current.add(msgId);
          setMessages(prev => [...prev, data.message]);
          scrollToBottom();
        }
      }
      setConversations(prev => {
        const existing = prev.find(c => c._id === data.conversationId);
        if (!existing) {
          // Brand-new conversation (first message from someone new) isn't in the locally
          // loaded list yet — fetch the full list instead of silently dropping the update.
          loadConversations();
          return prev;
        }
        const unreadCount =
          currentConversation && data.conversationId === currentConversation._id
            ? 0
            : (existing.unreadCount || 0) + 1;
        const updatedConversation = {
          ...existing,
          lastMessage: data.message,
          unreadCount,
          updatedAt: data.message?.createdAt || new Date().toISOString(),
        };
        return [updatedConversation, ...prev.filter(c => c._id !== data.conversationId)];
      });
    };
    socket.on('new-message', handleIncomingMessage);
    socket.on('new-conversation-message', handleIncomingMessage);
    socket.on('group-message', (data) => {
      const currentGroup = activeGroupRef.current;
      if (currentGroup && data.groupId === currentGroup._id) {
        const msgId = data.message._id;
        if (!processedMessageIds.current.has(msgId)) {
          processedMessageIds.current.add(msgId);
          setMessages(prev => [...prev, data.message]);
          scrollToBottom();
        }
      }
      setGroups(prev => {
        const existing = prev.find(g => g._id === data.groupId);
        if (!existing) return prev;
        const updatedGroup = {
          ...existing,
          lastMessage: data.message,
          updatedAt: data.message?.createdAt || new Date().toISOString(),
        };
        return [updatedGroup, ...prev.filter(g => g._id !== data.groupId)];
      });
    });
    socket.on('typing', (data) => {
      const currentConversation = activeConversationRef.current;
      if (currentConversation && data.conversationId === currentConversation._id) {
        setIsTyping(true); setTypingUser(data.userName);
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => { setIsTyping(false); setTypingUser(null); }, 2000);
      }
    });
    socket.on('user-online', (data) => {
      const currentConversation = activeConversationRef.current;
      if (data.userId && currentConversation) {
        const other = getOtherParticipant(currentConversation);
        if (other && other._id === data.userId) {
          setActiveConversation(prev => ({ ...prev, participants: prev.participants.map(p => p._id === data.userId ? { ...p, isOnline: true } : p) }));
        }
      }
    });
    socket.on('user-offline', (data) => {
      const currentConversation = activeConversationRef.current;
      if (data.userId && currentConversation) {
        const other = getOtherParticipant(currentConversation);
        if (other && other._id === data.userId) {
          setActiveConversation(prev => ({ ...prev, participants: prev.participants.map(p => p._id === data.userId ? { ...p, isOnline: false } : p) }));
        }
      }
    });
    return () => {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
      clearTimeout(typingTimeoutRef.current);
    };
  }, [user]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;

    if (activeConversation?._id) {
      socket.emit('join-conversation-room', activeConversation._id);
    }

    if (activeGroup?._id) {
      socket.emit('join-group-chat', activeGroup._id);
    }

    return () => {
      if (activeConversation?._id) {
        socket.emit('leave-conversation-room', activeConversation._id);
      }

      if (activeGroup?._id) {
        socket.emit('leave-group-chat', activeGroup._id);
      }
    };
  }, [activeConversation?._id, activeGroup?._id]);

  useEffect(() => { if (user) { loadConversations(); loadGroups(); loadAvailableUsers(); } }, [user]);
  useEffect(() => { if (activeConversation) { loadMessages(activeConversation._id); markAsRead(activeConversation._id); } }, [activeConversation?._id]);
  useEffect(() => {
    if (activeGroup) {
      markGroupAsRead(activeGroup._id);
      loadGroupMessages(activeGroup._id);
    }
  }, [activeGroup?._id]);
  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => { setSuccessMessage(null); setError(null); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const filteredConversations = conversations.filter(conv => {
    const other = getOtherParticipant(conv);
    return (other?.name || other?.username || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const filteredGroups = groups.filter(g =>
    g.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!user) {
    return (
      <div style={styles.authRequired}>
        <div style={styles.authCard}>
          <div style={styles.authIcon}><MessageCircle size={52} color="#32614d" /></div>
          <h2 style={styles.authTitle}>{t('pages.messages.signInTitle')}</h2>
          <p style={styles.authSubtitle}>{t('pages.messages.signInSubtitle')}</p>
          <button style={styles.authBtn} onClick={() => navigate('/registration')}>{t('pages.messages.logIn')}</button>
        </div>
      </div>
    );
  }

  const activeChat = activeConversation || activeGroup;
  const chatName = activeGroup ? activeGroup.name : getOtherParticipant(activeConversation)?.name;

  return (
    <>
      <style>{cssStyles}</style>
      <div style={styles.page}>

        {/* ── SIDEBAR ── hidden on mobile when chat is open */}
        <div style={styles.sidebar} className={`sidebar${mobileChatOpen ? ' sidebar-hidden' : ''}`}>
          <div style={styles.sidebarHeader}>
            <h1 style={styles.sidebarTitle}>{t('pages.messages.title')}</h1>
            <button style={styles.newChatBtn} className="icon-btn-primary"
              onClick={() => activeTab === 'direct' ? setShowNewChatModal(true) : navigate('/community')}
              title={t('pages.messages.newTooltip')}><Plus size={18} /></button>
          </div>
          <div style={styles.tabs}>
            <button style={{ ...styles.tab, ...(activeTab === 'direct' ? styles.tabActive : {}) }}
              onClick={() => { setActiveTab('direct'); setActiveConversation(null); setActiveGroup(null); setMobileChatOpen(false); navigate('/messages'); }}>
              <MessageSquare size={15} /><span>{t('pages.messages.tabDirect')}</span>
            </button>
            <button style={{ ...styles.tab, ...(activeTab === 'groups' ? styles.tabActive : {}) }}
              onClick={() => { setActiveTab('groups'); setActiveConversation(null); setActiveGroup(null); setMobileChatOpen(false); navigate('/messages'); }}>
              <UsersIcon size={15} /><span>{t('pages.messages.tabGroups')}</span>
            </button>
          </div>
          <div style={styles.searchWrap}>
            <Search size={15} style={styles.searchIcon} />
            <input style={styles.searchInput} className="search-input" placeholder={t('pages.messages.searchPlaceholder')}
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <div style={styles.list}>
            {activeTab === 'direct' ? (
              conversationsLoading ? (
                <div style={styles.loadingState}><div className="spinner" /><span>{t('pages.messages.loading')}</span></div>
              ) : filteredConversations.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={styles.emptyIcon}><MessageSquare size={36} color="#32614d" strokeWidth={1.5} /></div>
                  <p style={styles.emptyText}>{t('pages.messages.noConversations')}</p>
                  <button style={styles.emptyBtn} onClick={() => setShowNewChatModal(true)}>{t('pages.messages.startChat')}</button>
                </div>
              ) : filteredConversations.map(conv => {
                const other = getOtherParticipant(conv);
                const isActive = activeConversation?._id === conv._id;
                return (
                  <div key={conv._id} style={{ ...styles.convItem, ...(isActive ? styles.convItemActive : {}) }}
                    className="conv-item"
                    onClick={() => { setActiveConversation(conv); setMobileChatOpen(true); navigate(`/messages/dm/${conv._id}`); }}>
                    <div style={styles.avatarWrap}>
                      <UserAvatar person={other} style={styles.avatar} />
                      {other?.isOnline && <div style={styles.onlineDot} />}
                      {conv.unreadCount > 0 && <div style={styles.badge}>{conv.unreadCount}</div>}
                    </div>
                    <div style={styles.convInfo}>
                      <div style={styles.convRow}>
                        <span style={{ ...styles.convName, ...(isActive ? { color: '#fff' } : {}) }}>
                          {other?.name || other?.username || t('pages.messages.unknown')}
                          {other?.isVerified && <BadgeCheck size={14} color="#22c55e" strokeWidth={2.5} />}
                        </span>
                        <span style={{ ...styles.convTime, ...(isActive ? { color: 'rgba(255,255,255,0.7)' } : {}) }}>
                          {conv.lastMessage ? formatConversationTime(conv.lastMessage.createdAt) : ''}
                        </span>
                      </div>
                      <span style={{ ...styles.convPreview, ...(isActive ? { color: 'rgba(255,255,255,0.8)' } : {}) }}>
                        {conv.lastMessage?.metadata?.type === 'shared_post'
                          ? (conv.lastMessage?.content || t('pages.messages.sharedAPost')).substring(0, 40)
                          : conv.lastMessage?.content?.substring(0, 40) || t('pages.messages.noMessagesYet')}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              groupsLoading ? (
                <div style={styles.loadingState}><div className="spinner" /><span>{t('pages.messages.loading')}</span></div>
              ) : filteredGroups.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={styles.emptyIcon}><UsersIcon size={36} color="#32614d" strokeWidth={1.5} /></div>
                  <p style={styles.emptyText}>{t('pages.messages.noGroups')}</p>
                  <button style={styles.emptyBtn} onClick={() => navigate('/community')}>{t('pages.messages.browseGroups')}</button>
                </div>
              ) : filteredGroups.map(group => {
                const isActive = activeGroup?._id === group._id;
                const unread = isGroupUnread(group, user?._id);
                return (
                  <div key={group._id} style={{ ...styles.convItem, ...(isActive ? styles.convItemActive : {}) }}
                    className="conv-item"
                    onClick={() => {
                      markGroupAsRead(group._id);
                      setActiveGroup(group);
                      setMobileChatOpen(true);
                      navigate(`/messages/group/${group._id}`);
                    }}>
                    <div style={styles.avatarWrap}>
                      <GroupAvatar group={group} style={{ ...styles.avatar, borderRadius: '12px' }} />
                      {group.status === 'private' && <div style={styles.lockBadge}><Lock size={8} /></div>}
                      {unread && <div style={styles.badge}>1</div>}
                    </div>
                    <div style={styles.convInfo}>
                      <div style={styles.convRow}>
                        <span style={{ ...styles.convName, ...(isActive ? { color: '#fff' } : {}) }}>
                          {group.name}
                          {isGroupAdmin(group) && <Crown size={13} color="#ca8a04" />}
                        </span>
                        <span style={{ ...styles.convTime, ...(isActive ? { color: 'rgba(255,255,255,0.7)' } : {}) }}>
                          {group.lastMessage ? formatConversationTime(group.lastMessage.createdAt) : ''}
                        </span>
                      </div>
                      <span style={{ ...styles.convPreview, ...(isActive ? { color: 'rgba(255,255,255,0.8)' } : {}) }}>
                        {group.lastMessage?.content?.substring(0, 40) || group.description?.substring(0, 40) || t('pages.messages.noMessagesYet')}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── CHAT AREA ── hidden on mobile when no chat is open */}
        <div style={styles.chatArea} className={`chat-area${!mobileChatOpen ? ' chat-area-hidden' : ''}`}>
          {activeChat ? (
            <>
              {/* Chat Header */}
              <div style={styles.chatHeader}>
                <button style={styles.backBtn} className="icon-btn"
                  onClick={handleMobileBack}>
                  <ChevronLeft size={20} />
                </button>
                <div style={styles.chatHeaderInfo}>
                  <div style={styles.headerAvatarWrap}>
                    {activeGroup ? (
                      <GroupAvatar group={activeGroup} style={{ ...styles.avatar, width: 40, height: 40, borderRadius: '10px' }} />
                    ) : (
                      <UserAvatar person={getOtherParticipant(activeConversation)} style={{ ...styles.avatar, width: 40, height: 40 }} />
                    )}
                    {!activeGroup && getOtherParticipant(activeConversation)?.isOnline && <div style={styles.onlineDot} />}
                  </div>
                  <div>
                    <div style={styles.chatName}>
                      {chatName}
                      {!activeGroup && getOtherParticipant(activeConversation)?.isVerified && <BadgeCheck size={15} color="#22c55e" strokeWidth={2.5} />}
                      {activeGroup?.status === 'private' && <span style={styles.tagPrivate}><Lock size={10} style={{marginRight:2}} />{t('pages.messages.tagPrivate')}</span>}
                      {activeGroup && isGroupAdmin(activeGroup) && <span style={styles.tagAdmin}><Crown size={10} style={{marginRight:2}} />{t('pages.messages.tagAdmin')}</span>}
                    </div>
                    <div style={styles.chatSub}>
                      {activeGroup ? (
                        <>
                          <span>{t('pages.messages.membersCount', { count: activeGroup.membersCount || activeGroup.members?.length || 0 })}</span>
                          {isTyping && typingUser && <span style={styles.typingText}>· {t('pages.messages.someoneTyping', { name: typingUser })}</span>}
                        </>
                      ) : (
                        <>
                          <span style={{ color: getOtherParticipant(activeConversation)?.isOnline ? '#22c55e' : '#94a3b8' }}>
                            {getOtherParticipant(activeConversation)?.isOnline ? `● ${t('pages.messages.online')}` : `○ ${t('pages.messages.offline')}`}
                          </span>
                          {isTyping && <span style={styles.typingText}>· {t('pages.messages.typing')}</span>}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div style={styles.chatHeaderActions}>
                  {!activeGroup && (
                    <button style={styles.iconBtn} className="icon-btn"
                      onClick={() => { const u = getOtherParticipant(activeConversation); if (u) navigate(`/profile/${u._id}`); }}
                      title={t('pages.messages.viewProfileTooltip')}><User size={17} /></button>
                  )}
                  <button style={styles.iconBtn} className="icon-btn"
                    onClick={() => activeGroup ? setShowGroupInfo(true) : setShowConversationInfo(true)}
                    title={t('pages.messages.infoTooltip')}><Info size={17} /></button>
                  {activeGroup && isGroupMember(activeGroup) && (
                    <button style={{ ...styles.iconBtn, color: '#ef4444' }} className="icon-btn"
                      onClick={() => leaveGroup(activeGroup._id)} title={t('pages.messages.leaveGroupTooltip')}><LogOut size={17} /></button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div style={styles.messages} className="messages-scroll">
                {messagesLoading ? (
                  <div style={styles.centerState}><div className="spinner-lg" /><span style={{ color: '#94a3b8', marginTop: 12 }}>{t('pages.messages.loadingMessages')}</span></div>
                ) : messages.length === 0 ? (
                  <div style={styles.centerState}>
                    <div style={{ marginBottom: 16, color: '#cbd5e1' }}>{activeGroup ? <UsersIcon size={52} strokeWidth={1.2} /> : <MessageCircle size={52} strokeWidth={1.2} />}</div>
                    <p style={styles.emptyMsgTitle}>{t('pages.messages.noMessagesYet')}</p>
                    <p style={styles.emptyMsgSub}>{t('pages.messages.beFirstToSayHello')}</p>
                    {activeGroup && !isGroupMember(activeGroup) && (
                      <button style={styles.joinBtn} onClick={() => joinGroup(activeGroup._id)}>{t('pages.messages.joinGroupToChat')}</button>
                    )}
                  </div>
                ) : (
                  <>
                    {messages.map((message, idx) => {
                      const isMe = message.sender?._id === user._id;
                      const showAvatar = !isMe && (!messages[idx + 1] || messages[idx + 1]?.sender?._id !== message.sender?._id);
                      const isLiked = message.likes?.includes(user._id);
                      const isSharedPost = message.metadata?.type === 'shared_post';

                      return (
                        <div key={message._id} style={{ ...styles.msgRow, justifyContent: isMe ? 'flex-end' : 'flex-start' }} className="msg-row">
                          {!isMe && (
                            <div style={styles.msgAvatarCol}>
                              {showAvatar && <UserAvatar person={message.sender} style={styles.msgAvatar} />}
                            </div>
                          )}
                          <div style={styles.msgContent}>
                            {!isMe && activeGroup && showAvatar && (
                              <span style={styles.senderName}>
                                {message.sender?.name}
                                {message.sender?._id === activeGroup.createdBy && <Crown size={11} color="#ca8a04" style={{marginLeft:3}} />}
                              </span>
                            )}

                            {isSharedPost ? (
                              <div
                                onClick={() => message.metadata.postId && setOpenPostModalId(message.metadata.postId)}
                                className="shared-post-bubble"
                                style={{ borderRadius: 14, overflow: 'hidden', background: '#fff', border: '1.5px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer', maxWidth: 260, transition: 'box-shadow .2s, transform .15s' }}
                                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(50,97,77,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                              >
                                <div style={{ background: 'linear-gradient(135deg, #32614d, #2a5240)', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.3px' }}>📤 {t('pages.messages.sharedPost')}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 10px 5px' }}>
                                  <UserAvatar
                                    person={{ profileImage: message.metadata.postAuthorImage, name: message.metadata.postAuthorName }}
                                    style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #e2e8f0' }}
                                  />
                                  <span style={{ fontWeight: 700, fontSize: 12, color: '#1e293b' }}>{message.metadata.postAuthorName || t('pages.messages.unknown')}</span>
                                </div>
                                {(message.metadata.postImage || message.metadata.postImages?.[0]) && (
                                  <img src={getImageUrl(message.metadata.postImage || message.metadata.postImages[0])} alt="Post"
                                    style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />
                                )}
                                {message.metadata.postContent && (
                                  <div style={{ padding: '6px 10px', fontSize: 12, color: '#475569', lineHeight: 1.45 }}>
                                    {message.metadata.postContent.substring(0, 100)}{message.metadata.postContent.length > 100 ? '...' : ''}
                                  </div>
                                )}
                                <div style={{ padding: '6px 10px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, color: '#32614d', textAlign: 'center' }}>
                                  {t('pages.messages.tapToView')}
                                </div>
                              </div>
                            ) : (
                              <div style={{ ...styles.bubble, ...(isMe ? styles.bubbleSent : styles.bubbleReceived) }} className="bubble">
                                {message.content && <span style={{ whiteSpace: 'pre-wrap' }}>{message.content}</span>}
                                {message.image && <img src={getImageUrl(message.image)} alt="" style={styles.msgImage} />}
                              </div>
                            )}

                            <div style={{ ...styles.msgMeta, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                              <span style={styles.msgTime}>{formatTime(message.createdAt)}</span>
                              {isMe && (message.isRead
                                ? <CheckCheck size={13} color="#32614d" />
                                : <Check size={13} color="#94a3b8" />)}
                              <div style={styles.msgActions} className="msg-actions">
                                <button style={{ ...styles.msgActionBtn, ...(isLiked ? styles.msgActionBtnLiked : {}) }} onClick={() => handleMessageLike(message._id)} className="msg-action-btn">
                                  <Heart size={13} fill={isLiked ? '#ef4444' : 'none'} />
                                  {message.likes?.length > 0 && <span style={{ fontSize: 10 }}>{message.likes.length}</span>}
                                </button>
                                <button style={styles.msgActionBtn} onClick={() => handleMessageReply(message)} className="msg-action-btn">
                                  <Repeat size={13} />
                                </button>
                                  {isMe && (
                                    <button
                                      style={{ ...styles.msgActionBtn, background: 'rgba(239,68,68,0.10)', color: '#dc2626' }}
                                      onClick={() => handleDeleteMessage(message._id)}
                                      className="msg-action-btn msg-action-btn-danger"
                                      title={t('pages.messages.deleteMessageTooltip')}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input */}
<div style={{ ...styles.inputArea, position: 'relative' }}>
  {activeGroup && !isGroupMember(activeGroup) ? (
    <div style={styles.joinPrompt}>
      <span style={{ color: '#64748b', fontSize: '0.88rem' }}>{t('pages.messages.joinToSend')}</span>
      <button style={styles.joinBtn} onClick={() => joinGroup(activeGroup._id)}>{t('pages.messages.join')}</button>
    </div>
  ) : (
    <div>
      <div style={styles.inputRow}>
        <button style={styles.attachBtn} className="icon-btn" onClick={() => fileInputRef.current?.click()} title={t('pages.messages.attachTooltip')}>
          <Image size={19} />
        </button>
        <button
          ref={emojiButtonRef}
          style={{ ...styles.attachBtn, background: 'rgba(250,160,2,0.12)', color: '#32614d' }}
          className="icon-btn"
          onClick={() => setEmojiPickerOpen(v => !v)}
          title={t('pages.messages.emojisTooltip')}
        >
          <Smile size={19} />
        </button>
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*" style={{ display: 'none' }} />
        <div style={styles.inputWrap}>
          {filePreview && (
            <div style={styles.filePreview}>
              <img src={filePreview} alt="" style={styles.filePreviewImg} />
              <button style={styles.removeFile} onClick={() => { setSelectedFile(null); setFilePreview(null); }}><X size={13} /></button>
            </div>
          )}
          <textarea 
            value={newMessage} 
            onChange={e => { setNewMessage(e.target.value); handleTyping(); }}
            ref={textareaRef}
            onKeyPress={handleKeyPress}
            placeholder={t('pages.messages.messagePlaceholder', { name: chatName || '' })}
            style={styles.textarea} 
            className="msg-textarea" 
            rows={1} 
          />
        </div>
        <button
          style={{ ...styles.sendBtn, ...((!newMessage.trim() && !selectedFile) || sending ? styles.sendBtnDisabled : {}) }}
          onClick={activeGroup ? sendGroupMessage : sendMessage}
          disabled={(!newMessage.trim() && !selectedFile) || sending}
          className="send-btn"
        >
          {sending ? <div className="spinner-sm" /> : <Send size={17} />}
        </button>
      </div>
      {emojiPickerOpen && <EmojiPicker variant="panel" />}
    </div>
  )}
</div>
            </>
          ) : (
            <div style={styles.noChatState}>
              <div style={{ color: '#cbd5e1', marginBottom: 8 }}>{activeTab === 'direct' ? <MessageCircle size={60} strokeWidth={1.2} /> : <UsersIcon size={60} strokeWidth={1.2} />}</div>
              <h2 style={styles.noChatTitle}>{activeTab === 'direct' ? t('pages.messages.yourMessages') : t('pages.messages.yourGroups')}</h2>
              <p style={styles.noChatSub}>{activeTab === 'direct' ? t('pages.messages.selectConversation') : t('pages.messages.selectGroup')}</p>
              <button style={styles.noChatBtn}
                onClick={() => activeTab === 'direct' ? setShowNewChatModal(true) : navigate('/community')}>
                {activeTab === 'direct' ? `+ ${t('pages.messages.newConversation')}` : t('pages.messages.browseGroups')}
              </button>
            </div>
          )}
        </div>

        {/* ── MODALS ── */}
        {showNewChatModal && (
          <div style={styles.overlay} onClick={() => setShowNewChatModal(false)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()} className="modal-slide">
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{t('pages.messages.newMessageTitle')}</h3>
                <button style={styles.modalClose} onClick={() => setShowNewChatModal(false)}><X size={18} /></button>
              </div>
              <div style={styles.modalBody}>
                {availableUsers.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}><User size={36} color="#32614d" strokeWidth={1.5} /></div>
                    <p style={styles.emptyText}>{t('pages.messages.noUsersAvailable')}</p>
                    <small style={{ color: '#94a3b8', textAlign: 'center' }}>{t('pages.messages.mutualFollowersOnly')}</small>
                  </div>
                ) : availableUsers.map(u => (
                  <div key={u._id} style={styles.userItem} className="conv-item"
                    onClick={async () => {
                      try {
                        const existing = conversations.find(c => getOtherParticipant(c)?._id === u._id);
                        if (existing) { setActiveConversation(existing); setMobileChatOpen(true); navigate(`/messages/dm/${existing._id}`); }
                        else { const nc = await createNewConversation(u._id); setActiveConversation(nc); }
                        setShowNewChatModal(false);
                      } catch { setError(t('pages.messages.errStartConversation')); }
                    }}>
                    <UserAvatar person={u} style={{ ...styles.avatar, width: 42, height: 42 }} />
                    <div>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.92rem' }}>{u.name}</div>
                      <div style={{ fontSize: '0.82rem', color: '#94a3b8' }}>{u.bio?.substring(0, 50)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {showConversationInfo && activeConversation && (
          <div style={styles.overlay} onClick={() => setShowConversationInfo(false)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()} className="modal-slide">
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{t('pages.messages.conversationInfoTitle')}</h3>
                <button style={styles.modalClose} onClick={() => setShowConversationInfo(false)}><X size={18} /></button>
              </div>
              <div style={styles.modalBody}>
                <div style={styles.infoCard}>
                  <UserAvatar person={getOtherParticipant(activeConversation)} style={{ ...styles.avatar, width: 60, height: 60 }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>{getOtherParticipant(activeConversation)?.name}</div>
                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>@{getOtherParticipant(activeConversation)?.username}</div>
                    <div style={{ marginTop: 3, fontSize: '0.88rem', color: getOtherParticipant(activeConversation)?.isOnline ? '#22c55e' : '#94a3b8' }}>
                      {getOtherParticipant(activeConversation)?.isOnline ? `● ${t('pages.messages.online')}` : `○ ${t('pages.messages.offline')}`}
                    </div>
                  </div>
                </div>
                <button style={styles.dangerBtn} onClick={() => deleteConversation(activeConversation._id)}>
                  <Trash2 size={15} /> {t('pages.messages.deleteConversation')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showGroupInfo && activeGroup && (
          <div style={styles.overlay} onClick={() => setShowGroupInfo(false)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()} className="modal-slide">
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{t('pages.messages.groupInfoTitle')}</h3>
                <button style={styles.modalClose} onClick={() => setShowGroupInfo(false)}><X size={18} /></button>
              </div>
              <div style={styles.modalBody}>
                <div style={styles.infoCard}>
                  <GroupAvatar group={activeGroup} style={{ ...styles.avatar, width: 60, height: 60, borderRadius: '14px' }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>{activeGroup.name}</div>
                    <div style={{ color: '#64748b', fontSize: '0.88rem', display:'flex', alignItems:'center', gap:4 }}>
                      {t('pages.messages.membersCountDot', { count: activeGroup.membersCount || 0 })}{' '}
                      {activeGroup.status === 'public' ? <><Globe size={12} /> {t('pages.messages.public')}</> : <><Lock size={12} /> {t('pages.messages.tagPrivate')}</>}
                    </div>
                  </div>
                </div>
                {activeGroup.description && <div style={styles.descBox}>{activeGroup.description}</div>}
                {isGroupMember(activeGroup) ? (
                  <button style={styles.dangerBtn} onClick={() => leaveGroup(activeGroup._id)}><LogOut size={15} /> {t('pages.messages.leaveGroup')}</button>
                ) : (
                  <button style={styles.primaryBtn} onClick={() => joinGroup(activeGroup._id)}><UserPlus size={15} /> {t('pages.messages.joinGroup')}</button>
                )}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div style={{ ...styles.alert, ...styles.alertError }} className="alert-slide">
            <span>{localizeMessage(error)}</span>
            <button style={styles.alertClose} onClick={() => setError(null)}><X size={15} /></button>
          </div>
        )}
        {successMessage && (
          <div style={{ ...styles.alert, ...styles.alertSuccess }} className="alert-slide">
            <span>{successMessage}</span>
            <button style={styles.alertClose} onClick={() => setSuccessMessage(null)}><X size={15} /></button>
          </div>
        )}
      </div>

      {openPostModalId && (
        <PostModal postId={openPostModalId} isOpen={!!openPostModalId} onClose={() => setOpenPostModalId(null)} />
      )}
    </>
  );
}

const styles = {
  page: { display: 'flex', height: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', system-ui, sans-serif", overflow: 'hidden' },
  sidebar: { width: 320, background: '#fff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'transform .25s' },
  sidebarHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 18px 14px', borderBottom: '1px solid #f1f5f9' },
  sidebarTitle: { margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' },
  newChatBtn: { width: 34, height: 34, borderRadius: 10, border: 'none', background: '#32614d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  tabs: { display: 'flex', padding: '10px 14px', gap: 6, borderBottom: '1px solid #f1f5f9' },
  tab: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', borderRadius: 10, border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: '0.86rem', fontWeight: 500, transition: 'all .15s' },
  tabActive: { background: '#f0fdf4', color: '#32614d', fontWeight: 700 },
  searchWrap: { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', position: 'relative' },
  searchIcon: { position: 'absolute', left: 26, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
  searchInput: { width: '100%', padding: '8px 10px 8px 34px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box', color: '#1e293b' },
  list: { flex: 1, overflowY: 'auto', padding: '6px' },
  convItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '11px', borderRadius: 12, cursor: 'pointer', transition: 'all .15s', marginBottom: 2, border: '1.5px solid transparent' },
  convItemActive: { background: 'linear-gradient(135deg, #32614d, #2a5240)', border: '1.5px solid #32614d', boxShadow: '0 4px 16px rgba(50,97,77,0.2)' },
  avatarWrap: { position: 'relative', flexShrink: 0 },
  avatar: { width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' },
  onlineDot: { position: 'absolute', bottom: 1, right: 1, width: 10, height: 10, background: '#22c55e', borderRadius: '50%', border: '2px solid #fff' },
  badge: { position: 'absolute', top: -3, right: -3, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, minWidth: 17, height: 17, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', border: '2px solid #fff' },
  lockBadge: { position: 'absolute', bottom: -2, right: -2, background: '#fff', borderRadius: '50%', lineHeight: 1, fontSize: 10 },
  convInfo: { flex: 1, minWidth: 0 },
  convRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 },
  convName: { fontWeight: 600, fontSize: '0.9rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  convTime: { fontSize: '0.73rem', color: '#94a3b8', flexShrink: 0, marginLeft: 4 },
  convPreview: { fontSize: '0.81rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  loadingState: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1rem', gap: 12, color: '#94a3b8', fontSize: '0.88rem' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1rem', gap: 10 },
  emptyIcon: { fontSize: 42, marginBottom: 4 },
  emptyText: { color: '#475569', fontWeight: 600, margin: 0, fontSize: '0.9rem' },
  emptyBtn: { background: '#32614d', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' },
  chatArea: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#f8fafc', transition: 'transform .25s' },
  chatHeader: { display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0', gap: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' },
  backBtn: { width: 34, height: 34, border: 'none', background: '#f1f5f9', color: '#64748b', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all .15s' },
  chatHeaderInfo: { display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 },
  headerAvatarWrap: { position: 'relative', flexShrink: 0 },
  chatName: { fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chatSub: { fontSize: '0.78rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 },
  typingText: { color: '#32614d', fontStyle: 'italic' },
  tagPrivate: { background: '#f0fdf4', color: '#32614d', border: '1px solid #bbf7d0', borderRadius: 5, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, display:'inline-flex', alignItems:'center', flexShrink:0 },
  tagAdmin: { background: '#fefce8', color: '#ca8a04', border: '1px solid #fde047', borderRadius: 5, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, display:'inline-flex', alignItems:'center', flexShrink:0 },
  chatHeaderActions: { display: 'flex', gap: 5, flexShrink: 0 },
  iconBtn: { width: 34, height: 34, border: 'none', background: '#f1f5f9', color: '#64748b', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all .15s' },
  messages: { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 3 },
  centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8 },
  emptyMsgTitle: { fontWeight: 700, fontSize: '1.05rem', color: '#475569', margin: 0 },
  emptyMsgSub: { color: '#94a3b8', margin: 0, fontSize: '0.9rem' },
  msgRow: { display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 4 },
  msgAvatarCol: { width: 28, flexShrink: 0, display: 'flex', alignItems: 'flex-end' },
  msgAvatar: { width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #e2e8f0' },
  msgContent: { display: 'flex', flexDirection: 'column', maxWidth: '72%' },
  senderName: { fontSize: '0.74rem', fontWeight: 600, color: '#32614d', marginBottom: 2, paddingLeft: 3 },
  bubble: { padding: '9px 13px', borderRadius: 16, fontSize: '0.9rem', lineHeight: 1.45, wordBreak: 'break-word', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  bubbleSent: { background: 'linear-gradient(135deg, #32614d, #2a5240)', color: '#fff', borderTopRightRadius: 4 },
  bubbleReceived: { background: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', borderTopLeftRadius: 4 },
  msgImage: { maxWidth: 220, maxHeight: 220, borderRadius: 10, marginTop: 7, display: 'block', border: '1px solid rgba(0,0,0,0.06)' },
  msgMeta: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 },
  msgTime: { fontSize: '0.7rem', color: '#94a3b8' },
  msgActions: { display: 'flex', gap: 3, opacity: 0, transition: 'opacity .15s' },
  msgActionBtn: { width: 24, height: 24, border: 'none', background: '#f1f5f9', color: '#64748b', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: 2, transition: 'all .15s', padding: 0 },
  msgActionBtnLiked: { background: '#fee2e2', color: '#ef4444' },
  inputArea: { padding: '12px 14px', background: '#fff', borderTop: '1px solid #e2e8f0' },
  joinPrompt: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: 12, border: '1.5px solid #e2e8f0', gap: 10 },
  joinBtn: { background: '#32614d', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: '0.87rem', flexShrink: 0 },
  inputRow: { display: 'flex', alignItems: 'flex-end', gap: 8 },
  attachBtn: { width: 38, height: 38, border: 'none', background: '#f1f5f9', color: '#64748b', borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all .15s' },
  inputWrap: { flex: 1, display: 'flex', flexDirection: 'column', gap: 7 },
  filePreview: { position: 'relative', width: 70, height: 70, borderRadius: 9, overflow: 'hidden', border: '2px solid #e2e8f0' },
  filePreviewImg: { width: '100%', height: '100%', objectFit: 'cover' },
  removeFile: { position: 'absolute', top: 3, right: 3, width: 20, height: 20, border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 },
  textarea: { width: '100%', padding: '9px 12px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '0.9rem', resize: 'none', maxHeight: 110, fontFamily: 'inherit', lineHeight: 1.45, outline: 'none', transition: 'border .15s', color: '#1e293b', boxSizing: 'border-box' },
  sendBtn: { width: 42, height: 42, border: 'none', background: 'linear-gradient(135deg, #32614d, #2a5240)', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: '0 4px 14px rgba(50,97,77,0.3)', transition: 'all .2s' },
  sendBtnDisabled: { opacity: 0.45, cursor: 'not-allowed', boxShadow: 'none' },
  noChatState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'linear-gradient(135deg, #f8fafc, #f0f9f4)', gap: 10 },
  noChatTitle: { fontWeight: 800, fontSize: '1.4rem', color: '#1e293b', margin: 0, letterSpacing: '-0.5px' },
  noChatSub: { color: '#64748b', margin: 0, fontSize: '0.95rem' },
  noChatBtn: { marginTop: 8, background: '#32614d', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', boxShadow: '0 4px 16px rgba(50,97,77,0.25)' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(6px)', padding: '16px' },
  modal: { background: '#fff', borderRadius: 18, boxShadow: '0 24px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: 440, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px', background: 'linear-gradient(135deg, #32614d, #2a5240)', color: '#fff' },
  modalTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 700 },
  modalClose: { width: 30, height: 30, border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  modalBody: { padding: '14px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 },
  infoCard: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px', background: '#f8fafc', borderRadius: 12, border: '1.5px solid #e2e8f0' },
  userItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '11px', borderRadius: 11, cursor: 'pointer', transition: 'all .15s', border: '1.5px solid transparent' },
  descBox: { padding: '12px', background: '#f8fafc', borderRadius: 10, color: '#475569', fontSize: '0.88rem', lineHeight: 1.5, border: '1px solid #e2e8f0' },
  dangerBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', borderRadius: 11, border: '1.5px solid #fecaca', background: '#fee2e2', color: '#dc2626', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  primaryBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', borderRadius: 11, border: 'none', background: 'linear-gradient(135deg, #32614d, #2a5240)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  alert: { position: 'fixed', top: 16, right: 16, padding: '11px 16px', borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, maxWidth: 320, zIndex: 1001, fontWeight: 500, fontSize: '0.88rem', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' },
  alertError: { background: '#fee2e2', color: '#b91c1c', border: '1.5px solid #fecaca' },
  alertSuccess: { background: '#dcfce7', color: '#166534', border: '1.5px solid #bbf7d0' },
  alertClose: { background: 'none', border: 'none', color: 'inherit', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, padding: 0 },
  authRequired: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'linear-gradient(135deg, #f0fdf4, #f8fafc)', padding: '20px' },
  authCard: { background: '#fff', padding: '2.5rem', borderRadius: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.1)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, maxWidth: 340, width: '100%' },
  authIcon: { fontSize: 52 },
  authTitle: { margin: 0, fontWeight: 800, color: '#0f172a', fontSize: '1.4rem' },
  authSubtitle: { margin: 0, color: '#64748b', fontSize: '0.9rem' },
  authBtn: { marginTop: 6, background: '#32614d', color: '#fff', border: 'none', padding: '11px 28px', borderRadius: 11, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' },
};

const cssStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
  * { box-sizing: border-box; }
  .icon-btn:hover { background: #e2e8f0 !important; color: #32614d !important; }
  .icon-btn-primary:hover { background: #2a5240 !important; transform: scale(1.05); }
  .conv-item:hover:not([style*="linear-gradient"]) { background: #f0fdf4; border-color: #bbf7d0 !important; }
  .search-input:focus { border-color: #32614d !important; background: #fff !important; }
  .messages-scroll::-webkit-scrollbar { width: 4px; }
  .messages-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .msg-row:hover .msg-actions { opacity: 1 !important; }
  .msg-action-btn:hover { background: #32614d !important; color: #fff !important; }
  .msg-textarea:focus { border-color: #32614d !important; background: #fff !important; }
  .send-btn:hover:not(:disabled) { transform: translateY(-2px) scale(1.05) !important; box-shadow: 0 8px 24px rgba(50,97,77,0.4) !important; }
  .modal-slide { animation: modalIn .25s cubic-bezier(.34,1.56,.64,1); }
  @keyframes modalIn { from { opacity: 0; transform: scale(0.92) translateY(16px); } to { opacity: 1; transform: scale(1) translateY(0); } }
  .alert-slide { animation: alertIn .3s ease; }
  @keyframes alertIn { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }
  .spinner { width: 26px; height: 26px; border: 3px solid rgba(50,97,77,0.15); border-top-color: #32614d; border-radius: 50%; animation: spin 0.8s linear infinite; }
  .spinner-lg { width: 40px; height: 40px; border: 4px solid rgba(50,97,77,0.12); border-top-color: #32614d; border-radius: 50%; animation: spin 0.8s linear infinite; }
  .spinner-sm { width: 17px; height: 17px; border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Mobile: full screen sidebar / chat ── */
  @media (max-width: 768px) {
    /* Page is a stacking context */
    .sidebar {
      position: fixed !important;
      inset: 0 !important;
      width: 100% !important;
      z-index: 10;
      border-right: none !important;
      transform: translateX(0);
      transition: transform .25s ease;
    }
    .sidebar.sidebar-hidden {
      transform: translateX(-100%);
      pointer-events: none;
    }
    .chat-area {
      position: fixed !important;
      inset: 0 !important;
      width: 100% !important;
      z-index: 10;
      transform: translateX(100%);
      transition: transform .25s ease;
    }
    .chat-area:not(.chat-area-hidden) {
      transform: translateX(0);
    }
  }

  /* ── Emoji Picker ── */
  .emoji-picker {
    background: #fff;
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    box-shadow: 0 16px 50px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    z-index: 50;
  }

  .emoji-picker-panel {
    position: absolute;
    right: 10px;
    bottom: 90px;
    width: 340px;
    max-height: 280px;
    display: flex;
    flex-direction: column;
  }

  .emoji-picker-modal {
    width: 100%;
    max-height: 200px;
    display: flex;
    flex-direction: column;
  }

  .emoji-groups {
    display: flex;
    gap: 8px;
    padding: 10px 10px 6px;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .emoji-groups::-webkit-scrollbar {
    display: none;
  }

  .emoji-group-btn {
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    color: #32614d;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
  }

  .emoji-group-btn.active {
    background: #32614d;
    border-color: #32614d;
    color: #fff;
  }

  .emoji-grid {
    padding: 8px 10px 12px;
    overflow-y: auto;
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 6px;
  }

  .emoji-emoji-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid rgba(50, 97, 77, 0.12);
    background: #fff;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.12s ease, background 0.12s ease;
  }

  .emoji-emoji-btn:hover {
    transform: scale(1.06);
    background: rgba(50, 97, 77, 0.08);
  }

  @media (max-width: 480px) {
    .emoji-picker-panel {
      width: 92vw;
      right: 4vw;
      left: 4vw;
    }
    .emoji-grid {
      grid-template-columns: repeat(7, 1fr);
    }
  }
`;

export default Messages;
